import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarClock, Plus, Search, Loader2, Package, Truck, MapPin, CheckCircle2, AlertTriangle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';

/**
 * Merchant portal → Appointments. A merchant sees ONLY their own consignments' delivery slots and
 * books slots for their own LRs. Everything here goes to `/merchant/appointments`, which takes the
 * merchant from the login token — nothing on this screen chooses whose rows are shown.
 */

type State = 'PENDING' | 'BOOKED' | 'FAILED' | 'NOT_REQUIRED' | 'THIRD_PARTY' | 'DELIVERED';
interface Row {
  id: number; lrNo: string; awbNo?: string | null; orderNo?: string | null;
  consigneeName?: string | null; consigneeCity?: string | null; consigneePincode?: string | null;
  boxes?: number | null; courierName?: string | null; shipmentStatus?: string | null; edd?: string | null;
  appointmentDate?: string | null; appointmentTime?: string | null; poNumber?: string | null; poExpiryDate?: string | null;
  appointmentRef?: string | null; thirdPartyTitle?: string | null; thirdPartyDate?: string | null; thirdPartyTime?: string | null;
  state: State; message: string; canBook: boolean; cannotBookReason?: string | null;
}
interface Lookup {
  shipment: {
    lrNo: string; awbNo?: string | null; orderNo?: string | null; consigneeName?: string | null; consigneeCity?: string | null;
    consigneePincode?: string | null; boxes: number; courierName?: string | null; shipmentStatus: string; edd?: string | null;
    carrierConfirms: boolean;
  };
  appointment: Row | null; canBook: boolean; cannotBookReason?: string | null; slots: string[];
}

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'booked', label: 'Booked' },
  { key: 'failed', label: 'Failed' },
  { key: 'all', label: 'All' },
] as const;
type TabKey = typeof TABS[number]['key'];

const STATE_STYLE: Record<State, { label: string; cls: string }> = {
  PENDING: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  BOOKED: { label: 'Booked', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  FAILED: { label: 'Failed', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  NOT_REQUIRED: { label: 'Not required', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  THIRD_PARTY: { label: 'Third party', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  DELIVERED: { label: 'Delivered', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const errMsg = (e: any, fallback: string) => {
  const m = e?.response?.data?.message;
  return Array.isArray(m) ? m.join(' ') : (m || fallback);
};
const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
/** Today in India as YYYY-MM-DD — the server judges "past" in IST too. */
const istToday = () => new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);

export function MerchantAppointmentsPage() {
  const [tab, setTab] = useState<TabKey>('pending');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [bookLr, setBookLr] = useState<string | null>(null);
  const pageSize = 25;

  useEffect(() => { const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [tab]);

  const { data: summary } = useQuery({
    queryKey: ['merchant-appointments', 'summary'],
    queryFn: async () => (await api.get<{ pending: number; booked: number; failed: number; all: number }>('/merchant/appointments/summary')).data,
  });
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['merchant-appointments', 'list', tab, q, page],
    queryFn: async () => (await api.get<{ total: number; items: Row[] }>('/merchant/appointments', {
      params: { tab, page, pageSize, ...(q ? { lrNo: q } : {}) },
    })).data,
  });
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><CalendarClock size={20} /> Appointments</h1>
          <p className="text-sm text-slate-500">Delivery slots for your B2B consignments. Book a slot with the date, time window and PO your consignee gave you.</p>
        </div>
        <button onClick={() => setBookLr('')} className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90">
          <Plus size={16} /> Book appointment
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${tab === t.key ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
            {t.label}{summary ? <span className="ml-1.5 opacity-80">{summary[t.key]}</span> : null}
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search LR number(s)"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>
      ) : isError ? (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">{errMsg(error, 'Could not load appointments.')}</div>
      ) : !data?.items.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500 text-sm">
          No appointments here{q ? ` for "${q}"` : ''}.
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((r) => <ApptCard key={r.id} row={r} onBook={() => setBookLr(r.lrNo)} />)}
        </div>
      )}

      {total > pageSize && (
        <div className="flex items-center justify-end gap-2 text-sm text-slate-600">
          <span>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40"><ChevronLeft size={16} /></button>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40"><ChevronRight size={16} /></button>
        </div>
      )}

      {bookLr !== null && <BookModal initialLr={bookLr} onClose={() => setBookLr(null)} />}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 truncate">{value ?? '—'}</div>
    </div>
  );
}

function ApptCard({ row, onBook }: { row: Row; onBook: () => void }) {
  const st = STATE_STYLE[row.state];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-900">LR {row.lrNo}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
        {row.shipmentStatus && <span className="text-xs text-slate-500">· {row.shipmentStatus}</span>}
        <div className="ml-auto">
          {row.canBook && (
            <button onClick={onBook} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary-50">
              <CalendarClock size={14} /> {row.appointmentDate ? 'Reschedule' : 'Book slot'}
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-slate-600 mt-1">{row.message}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
        <Field label="Consignee" value={row.consigneeName} />
        <Field label="City" value={[row.consigneeCity, row.consigneePincode].filter(Boolean).join(' · ') || '—'} />
        <Field label="Courier" value={row.courierName} />
        <Field label="Slot" value={row.state === 'THIRD_PARTY'
          ? `${fmtDate(row.thirdPartyDate)}${row.thirdPartyTime ? ` · ${row.thirdPartyTime}` : ''}`
          : row.appointmentDate ? `${fmtDate(row.appointmentDate)} · ${row.appointmentTime ?? ''}` : '—'} />
        <Field label="PO" value={row.poNumber ? `${row.poNumber}${row.poExpiryDate ? ` (exp ${fmtDate(row.poExpiryDate)})` : ''}` : '—'} />
        <Field label="Boxes" value={row.boxes ?? '—'} />
      </div>
      {row.appointmentRef && <div className="text-xs text-slate-500 mt-2">Carrier appointment ID: {row.appointmentRef}</div>}
    </div>
  );
}

function BookModal({ initialLr, onClose }: { initialLr: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [lr, setLr] = useState(initialLr);
  const [checkedLr, setCheckedLr] = useState(initialLr || '');
  const [form, setForm] = useState({ appointmentDate: '', appointmentTime: '', poNumber: '', poExpiryDate: '', notes: '' });

  const lookup = useQuery({
    queryKey: ['merchant-appointments', 'lookup', checkedLr],
    enabled: !!checkedLr,
    retry: false,
    queryFn: async () => (await api.get<Lookup>('/merchant/appointments/lookup', { params: { lrNo: checkedLr } })).data,
  });

  // Prefill from the current appointment so a reschedule only changes what needs changing.
  useEffect(() => {
    const a = lookup.data?.appointment;
    if (!a) return;
    setForm((f) => ({
      ...f,
      appointmentTime: a.appointmentTime && lookup.data?.slots.includes(a.appointmentTime) ? a.appointmentTime : f.appointmentTime,
      poNumber: a.poNumber ?? f.poNumber,
      poExpiryDate: a.poExpiryDate && a.poExpiryDate >= istToday() ? a.poExpiryDate : f.poExpiryDate,
    }));
  }, [lookup.data]);

  const book = useMutation({
    mutationFn: async () => (await api.post<{ ok: boolean; message: string }>('/merchant/appointments', { lrNo: lookup.data!.shipment.lrNo, ...form })).data,
    onSuccess: (r) => {
      if (r.ok) toast.success(r.message); else toast.error(r.message);
      qc.invalidateQueries({ queryKey: ['merchant-appointments'] });
      onClose();
    },
    onError: (e: any) => toast.error(errMsg(e, 'Could not book the appointment.')),
  });

  const s = lookup.data?.shipment;
  const needsPo = !!s?.carrierConfirms;
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });
  const ready = !!form.appointmentDate && !!form.appointmentTime && (!needsPo || (!!form.poNumber.trim() && !!form.poExpiryDate));

  return (
    <Modal isOpen onClose={onClose} title="Book appointment" subtitle="For one of your B2B consignments" maxWidth="xl">
      <form onSubmit={(e) => { e.preventDefault(); if (lr.trim()) setCheckedLr(lr.trim()); }} className="flex gap-2">
        <input value={lr} onChange={(e) => setLr(e.target.value)} placeholder="LR number" autoFocus={!initialLr}
          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        <button type="submit" className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-200 hover:bg-slate-50">Check</button>
      </form>

      {lookup.isFetching && <div className="flex items-center text-sm text-slate-500 mt-4"><Loader2 className="animate-spin mr-2" size={16} /> Checking…</div>}
      {lookup.isError && <div className="mt-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm">{errMsg(lookup.error, 'Could not find that LR.')}</div>}

      {s && !lookup.isFetching && (
        <div className="mt-4 space-y-4">
          <div className="border border-slate-200 rounded-lg p-3 text-sm space-y-1">
            <div className="flex items-center gap-2 font-medium text-slate-900"><Package size={15} /> LR {s.lrNo}{s.orderNo ? ` · ${s.orderNo}` : ''}</div>
            <div className="flex items-center gap-2 text-slate-600"><MapPin size={14} /> {s.consigneeName ?? '—'}, {s.consigneeCity ?? '—'} {s.consigneePincode ?? ''}</div>
            <div className="flex items-center gap-2 text-slate-600"><Truck size={14} /> {s.courierName ?? '—'} · {s.shipmentStatus} · {s.boxes} box{s.boxes === 1 ? '' : 'es'}{s.edd ? ` · EDD ${fmtDate(s.edd)}` : ''}</div>
            {lookup.data?.appointment && (
              <div className="flex items-center gap-2 text-slate-600"><Clock size={14} /> Current: {lookup.data.appointment.message}
                {lookup.data.appointment.appointmentDate ? ` (${fmtDate(lookup.data.appointment.appointmentDate)} · ${lookup.data.appointment.appointmentTime ?? ''})` : ''}</div>
            )}
          </div>

          {!lookup.data!.canBook ? (
            <div className="flex gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-sm">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {lookup.data!.cannotBookReason}
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); if (ready) book.mutate(); }} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-sm">
                  <span className="text-slate-600">Appointment date *</span>
                  <input type="date" min={istToday()} value={form.appointmentDate} onChange={set('appointmentDate')} required
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg" />
                </label>
                <label className="text-sm">
                  <span className="text-slate-600">Time slot *</span>
                  <select value={form.appointmentTime} onChange={set('appointmentTime')} required
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg bg-white">
                    <option value="">Choose a slot</option>
                    {lookup.data!.slots.map((sl) => <option key={sl} value={sl}>{sl}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-slate-600">PO number{needsPo ? ' *' : ''}</span>
                  <input value={form.poNumber} onChange={set('poNumber')} required={needsPo} maxLength={100} placeholder="e.g. P5584399"
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg" />
                </label>
                <label className="text-sm">
                  <span className="text-slate-600">PO expiry date{needsPo ? ' *' : ''}</span>
                  <input type="date" min={form.appointmentDate || istToday()} value={form.poExpiryDate} onChange={set('poExpiryDate')} required={needsPo}
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg" />
                </label>
              </div>
              <label className="text-sm block">
                <span className="text-slate-600">Remarks (optional)</span>
                <textarea value={form.notes} onChange={set('notes')} maxLength={300} rows={2}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg" />
              </label>
              <p className="text-xs text-slate-500">
                {needsPo
                  ? `This slot is sent to ${s.courierName ?? 'the carrier'} for confirmation as soon as you book.`
                  : 'This slot is recorded for our delivery team, who will deliver in it.'}
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-slate-200">Cancel</button>
                <button type="submit" disabled={!ready || book.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white disabled:opacity-50">
                  {book.isPending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Book appointment
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </Modal>
  );
}
